import { sha256 } from "@noble/hashes/sha2.js";
import type {
  ArchiveFingerprint,
  ClassFingerprint,
  ClassInfo,
  MethodFingerprint,
} from "./types";

const encoder = new TextEncoder();

function hex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

function hashText(value: string) {
  return hex(sha256(encoder.encode(value)));
}

function referenceCategory(reference = "") {
  const value = reference.replaceAll(".", "/").toLowerCase();
  if (/minecraft.*(?:getsession|getaccesstoken|getusername|getuuid)/.test(value))
    return "minecraft-identity";
  if (/runtime\/exec|processbuilder/.test(value)) return "process";
  if (/http|urlconnection|okhttp|socket|datagramsocket/.test(value))
    return "network";
  if (/files\/(?:read|write|copy)|fileinputstream|fileoutputstream/.test(value))
    return "filesystem";
  if (/cipher|messagedigest|secretkeyspec|base64/.test(value)) return "crypto";
  if (/classloader|defineclass|class\/forname/.test(value))
    return "dynamic-load";
  if (/reflect|methodhandles|invoke/.test(value)) return "reflection";
  if (/zipinputstream|jarinputstream|gzipinputstream/.test(value))
    return "archive";
  if (/system\/getenv|system\/getproperty/.test(value)) return "environment";
  if (/robot|clipboard|screencapture|keyevent/.test(value)) return "collection";
  if (/stringbuilder|stringbuffer|string\/concat/.test(value)) return "string";
  if (/java\/|javax\/|kotlin\//.test(value)) return "runtime";
  if (/net\/minecraft\//.test(value)) return "minecraft";
  return reference ? "application-call" : "";
}

function normalizeInstruction(
  mnemonic: string,
  reference?: string,
  literal?: string | number,
) {
  const category = referenceCategory(reference);
  const literalKind =
    typeof literal === "number"
      ? "number"
      : typeof literal === "string"
        ? /https?:\/\//i.test(literal)
          ? "url"
          : /[\\/]/.test(literal)
            ? "path"
            : /^\s*[-/][a-z][\w-]*/i.test(literal)
              ? "command"
              : "string"
        : "";
  return [mnemonic, category, literalKind].filter(Boolean).join(":");
}

function featureSet(tokens: string[]) {
  const features = new Set<string>();
  for (const token of tokens) {
    const [, category] = token.split(":");
    if (category && !["runtime", "application-call"].includes(category)) {
      features.add(category);
    }
  }
  return Array.from(features).sort();
}

function simhash64(tokens: string[]) {
  if (!tokens.length) return "0000000000000000";
  const weights = new Int32Array(64);
  const grams =
    tokens.length < 3
      ? tokens
      : tokens.slice(0, -2).map((_, index) =>
          tokens.slice(index, index + 3).join("|"),
        );
  for (const gram of grams) {
    const digest = sha256(encoder.encode(gram));
    for (let bit = 0; bit < 64; bit += 1) {
      const set = (digest[Math.floor(bit / 8)] & (1 << (bit % 8))) !== 0;
      weights[bit] += set ? 1 : -1;
    }
  }
  let result = BigInt(0);
  for (let bit = 0; bit < 64; bit += 1) {
    if (weights[bit] >= 0) result |= BigInt(1) << BigInt(bit);
  }
  return result.toString(16).padStart(16, "0");
}

export function simhashSimilarity(left: string, right: string) {
  if (!/^[a-f0-9]{16}$/i.test(left) || !/^[a-f0-9]{16}$/i.test(right))
    return 0;
  let difference = BigInt(`0x${left}`) ^ BigInt(`0x${right}`);
  let distance = 0;
  while (difference) {
    distance += Number(difference & BigInt(1));
    difference >>= BigInt(1);
  }
  return Math.round((1 - distance / 64) * 1000) / 10;
}

export function buildFingerprints(classes: ClassInfo[]): {
  methods: MethodFingerprint[];
  classes: ClassFingerprint[];
  archive: ArchiveFingerprint;
} {
  const methodFingerprints: MethodFingerprint[] = [];
  const classFingerprints: ClassFingerprint[] = [];
  const featureCounts: Record<string, number> = {};

  for (const classInfo of classes) {
    const classSemanticTokens: string[] = [];
    const classOpcodeTokens: string[] = [];
    for (const method of classInfo.methods) {
      const opcodeTokens = method.instructions.map(
        (instruction) => instruction.mnemonic,
      );
      const semanticTokens = method.instructions.map((instruction) =>
        normalizeInstruction(
          instruction.mnemonic,
          instruction.reference,
          instruction.literal,
        ),
      );
      const features = featureSet(semanticTokens);
      for (const feature of features) {
        featureCounts[feature] = (featureCounts[feature] ?? 0) + 1;
      }
      classOpcodeTokens.push(...opcodeTokens, "<method>");
      classSemanticTokens.push(...semanticTokens, "<method>");
      methodFingerprints.push({
        id: `${classInfo.name}#${method.name}${method.descriptor}`,
        className: classInfo.name,
        entry: classInfo.path,
        method: method.name,
        descriptor: method.descriptor,
        instructionCount: method.instructions.length,
        opcodeSha256: hashText(opcodeTokens.join("\n")),
        semanticSha256: hashText(semanticTokens.join("\n")),
        simhash64: simhash64(semanticTokens),
        features,
      });
    }
    classFingerprints.push({
      className: classInfo.name,
      entry: classInfo.path,
      methodCount: classInfo.methods.length,
      instructionCount: classInfo.methods.reduce(
        (total, method) => total + method.instructions.length,
        0,
      ),
      normalizedSha256: hashText(classOpcodeTokens.join("\n")),
      semanticSha256: hashText(classSemanticTokens.join("\n")),
      simhash64: simhash64(classSemanticTokens),
      features: featureSet(classSemanticTokens),
    });
  }

  const orderedClasses = [...classFingerprints].sort((left, right) =>
    left.semanticSha256.localeCompare(right.semanticSha256),
  );
  const archiveSemanticTokens = methodFingerprints
    .flatMap((method) => [
      ...method.features.map((feature) => `feature:${feature}`),
      `shape:${Math.min(20, Math.floor(method.instructionCount / 8))}`,
    ])
    .sort();
  const archive: ArchiveFingerprint = {
    normalizedSha256: hashText(
      orderedClasses.map((item) => item.normalizedSha256).join("\n"),
    ),
    semanticSha256: hashText(
      orderedClasses.map((item) => item.semanticSha256).join("\n"),
    ),
    simhash64: simhash64(archiveSemanticTokens),
    classCount: classes.length,
    methodCount: methodFingerprints.length,
    instructionCount: methodFingerprints.reduce(
      (total, method) => total + method.instructionCount,
      0,
    ),
    featureCounts,
  };
  return {
    methods: methodFingerprints,
    classes: classFingerprints,
    archive,
  };
}
